import type { HtmlType, TsCodeType, EventType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { LeafBlock } from '../leaf-block.js'
import { Parameter } from '../../script/parameter.js'
import { ButtonTypes } from './button-block.js'
import type { ButtonType } from './button-block.js'
import { TextOrCode_assumeCode } from '../../data/text-or-code.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'

export const FileUploadDisplayStyles = {
	dropZone: 'Drop zone',
	button: 'Button / Floating Action Button',
} as const
export type FileUploadDisplayStyle = keyof typeof FileUploadDisplayStyles
export const FileUploadFabIcons = {
	upload: 'Upload',
	attach_file: 'Attach',
} as const
export type FileUploadIcon = keyof typeof FileUploadFabIcons

@jsonClass('FileUploadBlock')
export class FileUploadBlock extends LeafBlock {
	// @jsonProperty(String)
	// @EditorGeneralInput({
	// 	inputType: 'code',
	// 	order: 2,
	// 	label: 'Upload session',
	// 	codeLanguage: 'ts',
	// 	tsReturnType: () => 'Sys.FileUploadSession',
	// 	editorSize: 'singleline',
	// 	isBinding: true,
	// 	isMandatory: () => true,
	// })
	// uploadSessionCode?: TsCodeType = ''

	@jsonProperty()
	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 1,
		label: 'Multiple files',
		isBinding: false,
	})
	multiple: boolean = false

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: 'Allowed MIME types',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
	})
	allowedMimeTypes = new TextOrExpression_assumeExpression('*', 'T')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 3,
		label: 'Show drop zone',
		options: Object.entries(FileUploadDisplayStyles),
	})
	displayStyle: FileUploadDisplayStyle = 'dropZone'

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput<FileUploadBlock>({
		inputType: 'code',
		order: 4,
		label: 'Text',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
		alternativeContentAreaName: 'content',
	})
	textCode = new TextOrExpression_assumeExpression(
		'Drop files here to upload',
		'T',
	)

	@jsonProperty()
	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 5,
		label: 'Upload automatically',
		isBinding: false,
	})
	uploadAutomatically: boolean = false

	@jsonProperty(String)
	@ScreenEditorInput<FileUploadBlock>({
		inputType: 'dropdown',
		order: 6,
		label: 'Icon',
		options: Object.entries(FileUploadFabIcons),
		isVisible: (blocks) =>
			blocks[0].displayStyle != 'dropZone' &&
			!blocks[0].getContentArea('content'),
	})
	icon: FileUploadIcon = 'upload'

	@jsonProperty(String)
	@ScreenEditorInput<FileUploadBlock>({
		inputType: 'dropdown',
		order: 7,
		label: 'Button appearance',
		tab: 'Appearance',
		options: Object.entries(ButtonTypes),
		isVisible: (blocks) =>
			blocks[0].displayStyle != 'dropZone' &&
			!blocks[0].getContentArea('content'),
	})
	buttonType: ButtonType = 'mat-raised-button'

	constructor(init: Partial<FileUploadBlock> = {}) {
		super()
		this.exampleText = 'Some upload'
		this.init(init)
	}

	get allowedEventTypes(): EventType[] {
		return [
			{
				name: 'filesSelected',
				parameters: [
					new Parameter({
						name: 'files',
						type: 'File[]',
						defaultExpression: '[...$event]',
					}),
				],
			},
			{
				name: 'uploadStarted',
				parameters: [
					new Parameter({
						name: 'uploads',
						type: 'Sys.FileUpload[]',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'uploadFinished',
				parameters: [
					new Parameter({
						name: 'upload',
						type: 'Sys.FileUpload',
						defaultExpression: '$event',
					}),
				],
			},
		]
	}

	producePreview(): HtmlType {
		return `<input tabindex="-1" type="file" value="${this.calculateExampleText()}">`
	}

	getEditorCategory(): string {
		return 'Additional Form Fields'
	}

	canHaveColor(): boolean {
		return this.displayStyle == 'button'
	}
}
